import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _map from 'lodash/map';
import _get from 'lodash/get';
import _find from 'lodash/find';
import _forEach from 'lodash/forEach';
import _isEmpty from 'lodash/isEmpty';
import _debounce from 'lodash/debounce';
import classNames from 'classnames/bind';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import Close from 'yoda-core-components/lib/assets/svg/Close.svg';
import ArrowRight from 'yoda-core-components/lib/assets/svg/ArrowRight.svg';
import { detectisIE } from 'yoda-core-components/lib/helpers/Utils/Utils';
import config from './Header.config';
import AnalyticsAction from '../../actions/AnalyticsAction';
import OverlayAction from '../../actions/OverlayAction';
import * as styles from './SecondaryNavigationPanel.css';
import Link from '../Link/Link';
import shopDeptActions from '../../actions/ShowShopDepartmentAction';
import getNavigationData from './GetNavigationData';

const cx = classNames.bind(styles);
const iconRight = () => (
    <Icon
        iconType="svg"
        automationId="arrow-right"
        width="14px"
        height="14px"
        viewBox="0 0 14 14"
        className={cx('arrowRightIcon')}
        pathClassName={cx('arrowRight')}
    >
        <ArrowRight />
    </Icon>
);

const shopDepartmentMenuDefault = {
    firstLevelHover: false,
    hoverDepartmentData: '',
    lastBindedDept: '',
    hoverDept: '',
    hoverCategory: '',
    mousePosition: 1,
    displayLevel2Columns: 0,
};

export class ShopDepartmentMenu extends Component {
    static propTypes = {
        actions: PropTypes.object,
        shopDepartmentMenuInfo: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        subCategoryMax: PropTypes.number,
        shopDeptOverlay: PropTypes.number,
        enableSingleFlyerMouseIntention: PropTypes.bool,
        enableTestTriangle: PropTypes.bool,
        insideTriangleDelay: PropTypes.number, // when stay inside triangle for some time, trigger the hovered l1 (default 500ms)
        outsideTriangleDelay: PropTypes.number, // when move outside triangle, add an optional delay to trigger l1 (deault 0ms)
        updateMaxColumn: PropTypes.number, // maxColumn number that we can display against L1
        enableColumnStyleFlyer: PropTypes.bool,
    };

    static defaultProps = {
        actions: {},
        shopDepartmentMenuInfo: [],
        subCategoryMax: 0,
        shopDeptOverlay: null,
        enableSingleFlyerMouseIntention: false,
        enableTestTriangle: false,
        insideTriangleDelay: 500,
        outsideTriangleDelay: 0,
        updateMaxColumn: 0,
        enableColumnStyleFlyer: false,
    };

    /* istanbul ignore next */
    constructor(props) {
        super(props);

        this.state = { ...shopDepartmentMenuDefault };

        this.timeoutId = null;
        this.animationFrameId = null;
        this.currentMouseCoordinates = {};
        this.previousMouseCoordinates = {};
        this.activeSubMenuTopLeftCoordinates = {};
        this.activeSubMenuBottomLeftCoordinates = {};
        this.debouncedDisplayCategory = _debounce(
            this.displayCategory,
            this.props.outsideTriangleDelay
        );
    }

    componentDidMount() {
        /* istanbul ignore next */
        if (this.props.enableSingleFlyerMouseIntention) {
            document.addEventListener('mousemove', this.onWindowMouseMove);
        }
    }

    componentWillReceiveProps(nextProps) {
        /* istanbul ignore next */
        if (!this.props.outsideTriangleDelay && nextProps.outsideTriangleDelay) {
            this.debouncedDisplayCategory = _debounce(
                this.displayCategory,
                nextProps.outsideTriangleDelay
            );
        }
    }

    componentDidUpdate() {
        /* istanbul ignore next */
        if (this.props.enableSingleFlyerMouseIntention) {
            // after render; not called for the initial render
            this.refreshSubmenuCoords();
            // for testing only
            this.canvas = document.getElementById('canvas');
        }
    }

    componentWillUnmount() {
        /* istanbul ignore next */
        if (this.props.enableSingleFlyerMouseIntention) {
            document.removeEventListener('mousemove', this.onWindowMouseMove);

            // for testing only
            if (this.props.enableTestTriangle) {
                this.clearTriangle();
            }
        }
    }

    /* istanbul ignore next */
    onWindowMouseMove = (event) => {
        if (this.animationFrameId) {
            window.cancelAnimationFrame(this.animationFrameId);
        }

        this.animationFrameId = window.requestAnimationFrame(() => {
            this.saveMouseCoordinates(event.pageX, event.pageY);

            // for testing only
            if (this.props.enableTestTriangle) {
                this.clearTriangle();
                if (this.state.mousePosition === 1) {
                    this.drawTriangle();
                }
            }
        });
    };

    onLevel1MouseOver = (event) => {
        this.props.actions.showOverlay();
        const currentElemetOrPrevElement = event.target;
        const menuName = currentElemetOrPrevElement.textContent;

        /* istanbul ignore else */
        if (!this.props.enableSingleFlyerMouseIntention) {
            const { hoverDept } = this.state;
            if (!hoverDept) {
                this.displayCategory(menuName);
            }
        } else {
            // TODO: #TJ on move out of L1 clear it as well
            this.timeoutId && clearTimeout(this.timeoutId);

            if (this.shouldChangeActiveMenuItem()) {
                this.debouncedDisplayCategory(menuName);
            } else {
                // if moving within the triangle, but stay on an L1 for enough time, we still activate new category
                /* istanbul ignore next */
                this.timeoutId = setTimeout(() => {
                    if (this.state.mousePosition === 1) {
                        this.displayCategory(menuName);
                    }
                }, this.props.insideTriangleDelay);
            }
        }

        this.setState({
            hoverDept: menuName,
            hoverCategory: '',
            mousePosition: 1,
        });
    };

    onLevel1MouseOut = () => {
        /* istanbul ignore else */
        if (!this.props.enableSingleFlyerMouseIntention) {
            /* istanbul ignore next */
            const overlayDelay = this.props.shopDeptOverlay || config.desktop.mouseOverDelay;
            /* istanbul ignore next */
            setTimeout(() => {
                const { hoverDept } = this.state;
                this.state.mousePosition === 1 ? this.displayCategory(hoverDept) : '';
            }, overlayDelay);
        }
    };

    /* istanbul ignore next */
    onLevel2HeadingMouseOver = () => {
        this.setState({
            hoverCategory: '',
        });
    };

    onLevel2MouseEnter = () => {
        this.setState({
            mousePosition: 2,
        });
    };

    onLevel2MouseLeave = () => {
        // on moving mouse out, don't unset "menuSelected" style yet
        this.setState({
            mousePosition: 1,
            hoverDept: '',
        });
    };

    onLevel2MouseOver = (event) => {
        const currentElemetOrPrevElement = event.target;
        const menuName = currentElemetOrPrevElement.textContent;
        const { lastBindedDept, hoverCategory } = this.state;

        if (!hoverCategory) {
            this.displayCategory(lastBindedDept, menuName);
        }

        this.setState({
            hoverCategory: menuName,
        });
    };

    onLevel2MouseOut = () => {
        const overlayDelay = this.props.shopDeptOverlay || config.desktop.mouseOverDelay;
        /* istanbul ignore next */
        setTimeout(() => {
            const { lastBindedDept, hoverCategory } = this.state;

            if (hoverCategory && this.state.mousePosition === 2) {
                this.displayCategory(lastBindedDept, hoverCategory);
            }
        }, overlayDelay);
    };

    onLevel3MouseEnter = () => {
        this.setState({
            mousePosition: 3,
            hoverCategory: '',
        });
    };

    onLevel3MouseLeave = () => {
        this.setState({
            mousePosition: 2,
            hoverCategory: '',
        });
    };

    /* istanbul ignore next */
    onL1Click = (event, title, links, isInternalUrl) => {
        if (this.props.enableSingleFlyerMouseIntention) {
            this.timeoutId && clearTimeout(this.timeoutId);
            this.displayCategory(title);
        }
        return this.navigateurl(event, title, links, isInternalUrl);
    };

    /* istanbul ignore next */
    navigateurl(event, title, link, isInternalUrl = true, analyticData, targetWindow) {
        event.preventDefault();
        const eventName = analyticData ? analyticData.join(':') : title;
        const eventData = `top:${eventName}`;
        this.props.actions.triggerNavigationClick({
            linkName: eventData,
            isReload: !isInternalUrl,
        });

        if (link) {
            return {
                url: link,
                openInNewTab: targetWindow,
            };
        }
        return true; // redirect through <Link />
    }

    /* istanbul ignore next */
    resetMenu = () => {
        this.props.actions.removeOverlay();
        this.props.actions.hideShopDepartment();
    };

    displayCategory = (hoverDept, menuName) => {
        this.setState({
            lastBindedDept: hoverDept,
        });
        const { shopDepartmentMenuInfo, updateMaxColumn, enableColumnStyleFlyer } = this.props;
        const item = _find(
            shopDepartmentMenuInfo,
            (d) => d.title === hoverDept && !_isEmpty(d.departments)
        );
        if (item) {
            const { maxColumn } = item;
            const finalMaxColumn = updateMaxColumn || maxColumn;
            /* istanbul ignore next */
            const data = enableColumnStyleFlyer
                ? _map(
                      item.columnPositions,
                      (column, index) =>
                          column.length > 0 && (
                              <div className={cx(`numericColumnPosition${index + 1}`)}>
                                  {_map(column, (department) =>
                                      this.renderEachDepartment(department, item, menuName)
                                  )}
                              </div>
                          )
                  )
                : _map(item.departments, (department) =>
                      this.renderEachDepartment(department, item, menuName)
                  );

            /* istanbul ignore next */
            const megaMenuBlockClassName = enableColumnStyleFlyer
                ? cx('columnMegaMenuBlock')
                : cx('megaMenuBlock');
            /* eslint-disable jsx-a11y/no-static-element-interactions */
            const finalData = (
                <div
                    id="submenuElement"
                    className={megaMenuBlockClassName}
                    data-automation-id="megaMenuBlock"
                    onMouseEnter={this.onLevel2MouseEnter}
                    onMouseLeave={this.onLevel2MouseLeave}
                >
                    <div
                        id="subMenuClose"
                        data-automation-id="closeIcon"
                        className={cx('megaMenuCloseIconBlock')}
                        onClick={this.resetMenu}
                    >
                        <Icon
                            iconType="svg"
                            automationId="arrow-right"
                            width="32px"
                            height="32px"
                            viewBox="0 0 32 32"
                            className={cx('megaMenuCloseIcon')}
                            pathClassName={cx('megaMenuClose')}
                        >
                            <Close />
                        </Icon>
                        <span className={cx('megaMenuClosetext')}>close</span>
                    </div>
                    <div
                        data-automation-id="megaMenuSubMenu"
                        className={cx('subMenuLevel1ListBlock', `maxCol-${finalMaxColumn}`)}
                        id="subMenuLevel2"
                    >
                        {data}
                    </div>
                </div>
            );

            this.setState({
                hoverDepartmentData: finalData,
                firstLevelHover: true,
                displayLevel2Columns: finalMaxColumn,
            });
        } else {
            // empty L2
            this.setState({
                hoverDepartmentData: null,
                firstLevelHover: false,
            });
        }
    };

    displaySubCategory = (department, analyticData, menuName) => {
        /* istanbul ignore else */
        if (department.key === menuName) {
            const subCategoryList = [];
            /* istanbul ignore next */
            const truncatedSubcategory = this.props.subCategoryMax
                ? department.categories.slice(0, this.props.subCategoryMax)
                : department.categories;
            const columnPosition = department.numericColumnPosition;
            /* istanbul ignore next */
            const activityMapId = this.props.enableColumnStyleFlyer
                ? `subMenuLevel3:${columnPosition}`
                : 'subMenuLevel3';
            _forEach(truncatedSubcategory, (category) => {
                const eventData = [analyticData, department.key, category.name];
                const targetWindow = category.targetWindow === 'NW';
                const key = `${category.name}_megaMenuSubCategories`;
                subCategoryList.push(
                    <li
                        key={key}
                        id={activityMapId}
                        data-automation-id="megamenuInnerSubCategories"
                        className={cx('megaMenuSubCategories')}
                    >
                        <Link
                            href={category.targetUrl}
                            className={cx('subMenuLevelLink', { noCursor: !category.targetUrl })}
                            onClick={
                                /* istanbul ignore next */
                                (event) =>
                                    /* istanbul ignore next */
                                    this.navigateurl(
                                        event,
                                        category.name,
                                        category.targetUrl,
                                        true,
                                        eventData,
                                        targetWindow
                                    )
                            }
                            data-automation-id="sub-menu-level2"
                        >
                            {category.name}
                        </Link>
                    </li>
                );
            });

            return (
                <ul
                    data-automation-id="megaMenuInnerSubCategories"
                    className={cx('megaMenuInnerSubCategories')}
                >
                    {subCategoryList}
                </ul>
            );
        }

        /* istanbul ignore next */
        return null;
    };

    /* istanbul ignore next */
    saveMouseCoordinates = (x, y) => {
        // mousemove event gets triggered even when pageX/pageY are unchanged, hence the sanity check
        if (x !== this.currentMouseCoordinates.x && y !== this.currentMouseCoordinates.y) {
            this.previousMouseCoordinates = {
                x: this.currentMouseCoordinates.x,
                y: this.currentMouseCoordinates.y,
            };
            this.currentMouseCoordinates = { x, y };
        }
    };

    // Return `true` if there currently isn't an active menu item, or if
    // `currentMouseCoordinates` is outside of the triangle drawn between
    // `previousMouseCoordinates`, `activeSubMenuTopLeftCoordinates` and
    // `activeSubMenuBottomLeftCoordinates`.
    /* istanbul ignore next */
    shouldChangeActiveMenuItem = () => {
        const previousTopGradient = this.calculateGradient(
            this.previousMouseCoordinates,
            this.activeSubMenuTopLeftCoordinates
        );
        const currentTopGradient = this.calculateGradient(
            this.currentMouseCoordinates,
            this.activeSubMenuTopLeftCoordinates
        );
        const previousBottomGradient = this.calculateGradient(
            this.previousMouseCoordinates,
            this.activeSubMenuBottomLeftCoordinates
        );
        const currentBottomGradient = this.calculateGradient(
            this.currentMouseCoordinates,
            this.activeSubMenuBottomLeftCoordinates
        );

        if (
            isNaN(previousTopGradient) ||
            isNaN(currentTopGradient) ||
            isNaN(previousBottomGradient) ||
            isNaN(currentBottomGradient)
        ) {
            return true; // if for some reason flyout is not shown, return true
        }

        // if currently no L1 is triggered, or moving out of the triangle, then trigger the hovered L1
        return (
            !this.state.hoverDept ||
            previousTopGradient < currentTopGradient ||
            previousBottomGradient > currentBottomGradient
        );
    };

    /* istanbul ignore next */
    refreshSubmenuCoords = () => {
        // use a more precise triangle
        const categoryElements = document.querySelectorAll(
            '[data-automation-id="megaMenuInnerBlock"]'
        );
        if (categoryElements) {
            let bottomLeftSlope;
            let bottomLeftCoordinate;
            // iterate htmlCollection
            [].forEach.call(categoryElements, (categoryElement) => {
                const currentBottomLeftCoordinate = this.calculateBottomLeftCoordinate(
                    categoryElement
                );
                if (!currentBottomLeftCoordinate) return;
                // find the max slope for the bottom edge of the triangle
                const currentBottomLeftSlope = this.calculateGradient(
                    this.currentMouseCoordinates,
                    {
                        x: currentBottomLeftCoordinate.x,
                        y: currentBottomLeftCoordinate.y,
                    }
                );
                if (
                    typeof bottomLeftSlope === 'undefined' ||
                    bottomLeftSlope < currentBottomLeftSlope
                ) {
                    bottomLeftSlope = currentBottomLeftSlope;
                    bottomLeftCoordinate = currentBottomLeftCoordinate;
                }
            });
            if (!bottomLeftCoordinate) return;
            this.activeSubMenuBottomLeftCoordinates = {
                x: bottomLeftCoordinate.x,
                y: bottomLeftCoordinate.y,
            };
        }

        const submenuElement = document.getElementById('submenuElement');
        if (submenuElement) {
            const topLeftCoordinate = this.calculateTopLeftCoordinate(submenuElement);
            this.activeSubMenuTopLeftCoordinates = {
                x: topLeftCoordinate.x,
                y: topLeftCoordinate.y,
            };
        }
    };

    /* istanbul ignore next */
    drawTriangle = () => {
        if (this.canvas && this.canvas.getContext) {
            const ctx = this.canvas.getContext('2d');
            const rect = this.canvas.getBoundingClientRect(); // abs. size of element
            const scaleX = this.canvas.width / rect.width; // relationship bitmap vs. element for X
            const scaleY = this.canvas.height / rect.height;
            ctx.beginPath();
            ctx.moveTo(
                (this.currentMouseCoordinates.x - rect.left) * scaleX,
                (this.currentMouseCoordinates.y - rect.top) * scaleY
            );
            ctx.lineTo(
                (this.activeSubMenuTopLeftCoordinates.x - rect.left) * scaleX,
                (this.activeSubMenuTopLeftCoordinates.y - rect.top) * scaleY
            );
            ctx.lineTo(
                (this.activeSubMenuBottomLeftCoordinates.x - rect.left) * scaleX,
                (this.activeSubMenuBottomLeftCoordinates.y - rect.top) * scaleY
            );
            ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
            ctx.fill();
        }
    };

    /* istanbul ignore next */
    clearTriangle = () => {
        if (this.canvas && this.canvas.getContext) {
            const ctx = this.canvas.getContext('2d');
            ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        }
    };

    /** utilities */
    /* istanbul ignore next */
    calculateTopLeftCoordinate = (element) => {
        const rect = element.getBoundingClientRect();
        const x = rect.left + (window.pageXOffset || document.documentElement.scrollLeft);
        const y = rect.top + (window.pageYOffset || document.documentElement.scrollTop);
        return { x, y };
    };

    /* istanbul ignore next */
    calculateBottomLeftCoordinate = (element) => {
        const rect = element.getBoundingClientRect();
        const x = rect.left + (window.pageXOffset || document.documentElement.scrollLeft);
        const y = rect.top + (window.pageYOffset || document.documentElement.scrollTop);
        return { x, y: y + element.offsetHeight };
    };

    /* istanbul ignore next */
    calculateGradient = (A, B) => (B.y - A.y) / (B.x - A.x);
    /** utilities end */

    renderEachDepartment = (department, item) => {
        const { title } = item;
        const { key, links, targetWindow: deptTargetWindow, numericColumnPosition } = department;
        const eventData = [title, department.key];
        const activityMapId = this.props.enableColumnStyleFlyer
            ? `sub-menu-level2:${numericColumnPosition}`
            : 'sub-menu-level2';
        const targetWindow = deptTargetWindow === 'NW';

        return (
            <div data-automation-id="megaMenuInnerBlock" className={cx('megaMenuInnerBlock')}>
                <h4
                    className={cx('megaMenuTitle')}
                    data-automation-id="sub-menu-level1"
                    id={activityMapId}
                >
                    <Link
                        href={links}
                        className={cx('subMenuLevelLink', 'subMenuLevel1Link', {
                            noCursor: !links,
                        })}
                        onClick={
                            /* istanbul ignore next */
                            (e) => this.navigateurl(e, key, links, true, eventData, targetWindow)
                        }
                    >
                        {key}
                    </Link>
                </h4>
                {this.displaySubCategory(department, title, department.key)}
            </div>
        );
    };

    render() {
        const { shopDepartmentMenuInfo, enableColumnStyleFlyer } = this.props;
        const { firstLevelHover, lastBindedDept, displayLevel2Columns } = this.state;

        /* istanbul ignore else */
        if (shopDepartmentMenuInfo) {
            const columnMegamenu = firstLevelHover
                ? cx(
                      'largeSubMenu',
                      'megaMenu',
                      'megaMenuWidth',
                      `displayL2Column-${displayLevel2Columns}`
                  )
                : cx('largeSubMenu', 'megaMenu');
            const megaMenu = firstLevelHover
                ? cx('largeSubMenu', 'megaMenu', 'megaMenuWidth')
                : cx('largeSubMenu', 'megaMenu');
            const menuStyle = enableColumnStyleFlyer ? cx(columnMegamenu) : cx(megaMenu);
            const submenuStyle = cx('subMenuList');

            return (
                <>
                    {/* single flyout - for testing only: */}
                    {this.props.enableTestTriangle && (
                        <canvas
                            id="canvas"
                            height="576"
                            width="944"
                            style={{
                                position: 'absolute',
                                top: '56px',
                                left: 0,
                                zIndex: 999,
                                pointerEvents: 'none',
                            }}
                        />
                    )}

                    <div className={menuStyle}>
                        <div className={submenuStyle} data-automation-id="menu-list">
                            {/* " level1Block " Class for the hover Functionality for BG White once the mouse
                        pointer enteres onto the UL Block  Eg: Shop Dept => Men => Block */}
                            <ul
                                className={cx('subMenuListBlock')}
                                id="subMenuLevel1"
                                data-automation-id="menu-list-block"
                            >
                                {_map(shopDepartmentMenuInfo, (item, i) => {
                                    const { title, links, isInternalUrl, departments } = item;
                                    const styleName =
                                        item.title !== 'clearance'
                                            ? cx('menuItemLink', { noCursor: !links })
                                            : cx('menuItemLink', 'clearanceMenuLink', {
                                                  noCursor: !links,
                                              });
                                    const secondLevelStyle =
                                        item.title === lastBindedDept
                                            ? cx('menuItem', 'menuSelected')
                                            : cx('menuItem');

                                    return (
                                        <li
                                            className={secondLevelStyle}
                                            data-automation-id={`menu-item-${i}`}
                                        >
                                            {/* " menuSelected " Class for the hover bold and BG white
                                        Functionality while the mouse pointer hover the level1 List item
                                        Eg: Shop Dept => Men */}
                                            <Link
                                                href={links}
                                                className={styleName}
                                                onClick={(event) =>
                                                    this.onL1Click(
                                                        event,
                                                        title,
                                                        links,
                                                        isInternalUrl
                                                    )
                                                }
                                                data-automation-id={`menu-item-link-${i}`}
                                                onMouseOver={this.onLevel1MouseOver}
                                                onMouseOut={this.onLevel1MouseOut}
                                            >
                                                {title}
                                                {!_isEmpty(departments) && iconRight()}
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                        {this.state.hoverDepartmentData}
                    </div>
                </>
            );
        }
        /* istanbul ignore next */
        return null;
    }
}

const mapStateToProps = ({ context, IrisGlobalNavigation: departmentVisualNav = [] }) => {
    const shopDeptOverlay = _get(context, 'preferences.shopDeptOverlay', null);
    const updateMaxColumn = _get(context, 'preferences.updateMaxColumn', null);
    const subCategoryMax = _get(context, 'preferences.singleFlyer.subCategoryMax', 0);
    const enableColumnStyleFlyer = _get(context, 'featureFlags.enableColumnStyleFlyer', false);

    // for mouse intention, have a seperate flag for IE
    let enableSingleFlyerMouseIntention;
    /* istanbul ignore next */
    if (detectisIE()) {
        enableSingleFlyerMouseIntention = _get(
            context,
            'featureFlags.enableSingleFlyerMouseIntentionForIE',
            false
        );
    } else {
        enableSingleFlyerMouseIntention = _get(
            context,
            'featureFlags.enableSingleFlyerMouseIntention',
            false
        );
    }
    // enableTestTriangle comes as a query string
    const queryParams = !__SERVER__ && window.location.href.split('?')[1];
    /* istanbul ignore next */
    const enableTestTriangle = queryParams
        ? queryParams.split('&').filter((query) => query.indexOf('enableTestTriangle') === 0)
              .length > 0
        : false;
    const insideTriangleDelay = _get(context, 'preferences.singleFlyer.insideTriangleDelay', 500);
    const outsideTriangleDelay = _get(context, 'preferences.singleFlyer.outsideTriangleDelay', 0);

    /* istanbul ignore else */
    if (departmentVisualNav.length === 0) {
        departmentVisualNav = getNavigationData(_get(context, 'preferences.departments', []));
    }

    return {
        shopDeptOverlay,
        shopDepartmentMenuInfo: departmentVisualNav,
        subCategoryMax,
        enableSingleFlyerMouseIntention,
        insideTriangleDelay,
        enableTestTriangle,
        outsideTriangleDelay,
        queryParams,
        updateMaxColumn,
        enableColumnStyleFlyer,
    };
};

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        { ...AnalyticsAction, ...OverlayAction, ...shopDeptActions },
        dispatch
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(ShopDepartmentMenu);
